/**
 * The elements the browser's Tab key would actually stop at inside `root`, in DOM order.
 *
 * Why this exists (UI review 2026-10-23, call-setup B04 + in-call B04): the hand-built focus loops matched a raw selector
 * (`button:not([disabled]), input, [tabindex]:not([tabindex="-1"]) ...`). That selector also matched
 *  - a `display: none` file input (BackgroundEffectPicker's upload input): Shift+Tab from the first control called
 *    focus() on it, which does nothing, so focus stayed stuck on Close;
 *  - roving-tabindex radio buttons (`tabIndex = -1` on a `<button>`): the selector's `button` arm ignores tabindex;
 *  - controls inside a closed `<details>` (only its summary is visible).
 * The "last" element of such a list is not the last Tab stop, so forward Tab escaped the dialog and reverse Tab stuck.
 *
 * Rules: not disabled, `tabIndex >= 0`, not `type="hidden"`, not inside `[inert]` or `[hidden]`, not hidden by
 * `display: none` / `visibility: hidden` on itself or an ancestor (up to `root`), and inside a closed `<details>` only
 * that details' own summary counts.
 */
const CANDIDATES =
  'summary, a[href], button, input, select, textarea, iframe, audio[controls], video[controls], [contenteditable=""], [contenteditable="true"], [tabindex]';

function hiddenByStyle(el: Element, root: Element): boolean {
  const view = el.ownerDocument?.defaultView;
  if (!view) return false;
  let node: Element | null = el;
  while (node) {
    const style = view.getComputedStyle(node);
    if (style.display === 'none') return true;
    // visibility is inherited, so the element's own computed value already answers for its ancestors.
    if (node === el && (style.visibility === 'hidden' || style.visibility === 'collapse')) return true;
    if (node === root) break;
    node = node.parentElement;
  }
  return false;
}

/** The tab index the browser uses: an explicit attribute wins; every candidate without one is natively focusable. */
function tabIndexOf(el: HTMLElement): number {
  const raw = el.getAttribute('tabindex');
  if (raw === null) return 0;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isTabbable(el: HTMLElement, root: Element = el.ownerDocument.documentElement): boolean {
  if ((el as HTMLButtonElement).disabled) return false;
  if (tabIndexOf(el) < 0) return false;
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  if (el.closest('[inert]') || el.closest('[hidden]')) return false;
  const closedDetails = el.closest('details:not([open])');
  if (closedDetails && !(el.tagName === 'SUMMARY' && el.parentElement === closedDetails)) return false;
  return !hiddenByStyle(el, root);
}

export function getTabbableElements(root: Element): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(CANDIDATES)).filter((el) => isTabbable(el, root));
}
